import React from "react";
import catalog from "../../content/search.json";
import norwegian from "../../lang/no.json";

const { business, pages, projects } = catalog;
const pageByPath = new Map(pages.map((page) => [page.path, page]));
const projectById = new Map(projects.map((project) => [project.id, project]));

export function PageLinks({ paths, label = "Relaterte sider" }) {
  return (
    <nav className="search-links" aria-label={label}>
      {paths.map((path) => (
        <a key={path} href={path}>{pageByPath.get(path).label}</a>
      ))}
    </nav>
  );
}

export function MarketingHero({ page }) {
  return (
    <section id="hero" className="search-hero search-container">
      <div className="search-hero-copy">
        <h1>{page.heading.split("\n").map((line, index) => <React.Fragment key={line}>{index > 0 ? <br /> : null}{line}</React.Fragment>)}</h1>
        <p>{page.lead}</p>
        <div className="search-actions">
          <a className="search-button" href="/#contact">Be om tilbud</a>
          <a className="search-text-link" href={page.kind === "home" ? "/#products" : page.path === "/hastebestilling" ? "/services/tekstiltrykk" : "/hastebestilling"}>
            {page.kind === "home" ? "Se produktene" : page.path === "/hastebestilling" ? "Se trykkløsninger" : "Kort frist?"}
          </a>
        </div>
      </div>
      <img className="search-hero-image" src={page.image} alt={page.imageAlt} width="540" height="540" fetchpriority="high" />
    </section>
  );
}

export function MarketingIntro({ page }) {
  return (
    <section id="trykk-og-profilering" className="search-intro search-container">
      <div className="search-hero-copy">
        <h2>Godt trykk.<br />Raskt levert.</h2>
        <p>{page.lead}</p>
        <div className="search-actions">
          <a className="search-button" href="/#contact">Be om tilbud</a>
          <a className="search-text-link" href="/#products">Se produktene</a>
        </div>
      </div>
      <img className="search-hero-image" src={page.image} alt={page.imageAlt} width="540" height="540" loading="lazy" />
    </section>
  );
}

// The initial HTML preserves the established homepage hero before the
// original interactive Material UI hero takes over in the browser.
function StaticBrandHero() {
  return (
    <section id="hero" className="search-brand-hero">
      <span className="search-brand-prefix">Viktig å ha ett godt</span>
      <h1><span>Førsteinntrykk</span></h1>
      <p>{norwegian.Home.Hero.Slogan}</p>
      <div className="search-brand-actions">
        <a className="search-brand-login" href="/login">{norwegian.LogIn}</a>
        <a className="search-brand-login search-brand-contact" href="/#contact">{norwegian.Header.Contact}</a>
      </div>
    </section>
  );
}

export function ProductSearchIntro({ page }) {
  return (
    <section className="search-product-intro search-container">
      <nav aria-label="Brødsmuler" className="search-breadcrumb"><a href="/">Førsteinntrykk</a><span> / {page.label}</span></nav>
      <h1>{page.heading}</h1>
      <p>{page.lead}</p>
    </section>
  );
}

export function MarketingBody({ page }) {
  return (
    <div className="search-body search-container">
      {page.sections.map((section) => (
        <section key={section.heading} className="search-section">
          <h2>{section.heading}</h2>
          <div>
            {section.paragraphs.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
            {section.links ? <PageLinks paths={section.links} label={section.heading} /> : null}
            {section.externalLinks ? (
              <nav className="search-links" aria-label={`${section.heading}, eksterne sider`}>
                {section.externalLinks.map((link) => <a key={link.href} href={link.href}>{link.label}</a>)}
              </nav>
            ) : null}
          </div>
        </section>
      ))}
      {page.projectIds ? (
        <section className="search-section" aria-labelledby="search-projects-title">
          <h2 id="search-projects-title">Arbeid vi har levert</h2>
          <div>
            {page.projectIds.map((id) => {
              const project = projectById.get(id);
              return (
                <article className="search-project" key={id}>
                  <img src={project.image} alt={project.imageAlt} width={project.width} height={project.height} loading="lazy" />
                  <div>
                    <h3>{project.title}</h3>
                    <p>{project.description}</p>
                    <p>{project.detail}</p>
                    <a className="search-text-link" href={project.servicePath === page.path ? "/#contact" : project.servicePath}>
                      {project.servicePath === page.path ? "Be om tilbud" : `Les om ${pageByPath.get(project.servicePath).label.toLowerCase()}`}
                    </a>
                  </div>
                </article>
              );
            })}
            <p>Tidene beskriver arbeidet i disse eksemplene, ikke total leveringstid fra bestilling. Produkt, antall, tilgjengelige materialer, godkjent trykkfil og kapasitet avgjør fristen for en ny bestilling. Henting eller transport avtales separat.</p>
          </div>
        </section>
      ) : null}
      <section className="search-section search-faq" aria-labelledby="search-faq-title">
        <h2 id="search-faq-title">Ofte stilte spørsmål</h2>
        <div>{page.faqs.map((faq) => (
          <details key={faq.question}>
            <summary>{faq.question}</summary>
            <p>{faq.answer}</p>
          </details>
        ))}</div>
      </section>
      <section className="search-section">
        <h2>Finn løsningen din</h2>
        <PageLinks paths={page.related} label="Finn løsningen din" />
      </section>
    </div>
  );
}

export function SearchContact() {
  return (
    <section className="search-contact search-container" id="contact">
      <h2>Fortell oss hva du trenger.</h2>
      <p>Send produkt, antall og ønsket frist. Vi hjelper deg med en løsning og et konkret tilbud.</p>
      <div className="search-actions">
        <a className="search-button" href={`mailto:${business.email}`}>Be om tilbud</a>
        <a className="search-text-link" href={`tel:${business.phone}`}>Ring {business.phoneLabel}</a>
      </div>
      <p className="search-contact-email"><a href={`mailto:${business.email}`}>{business.email}</a></p>
      <p>
        <a className="search-text-link" href={business.mapsUrl}>{business.addressLabel}</a><br />
        Normalt mandag–fredag {business.openingHours.opens}–{business.openingHours.closes}.<br />
        På rolige dager kan vi stenge kl. 15:00. Avtal henting eller ring før du kommer.
      </p>
    </section>
  );
}

// These components also render at build time. Keep them free of browser APIs,
// app state and API requests so crawlers and visitors receive the same copy.
export function StaticPublicPage({ page }) {
  return (
    <div className="search-static-shell">
      <header className="search-static-header search-container">
        <a href="/" aria-label="Førsteinntrykk, forsiden"><img src="/images/brand-mark.svg" alt="" width="36" height="36" /><strong>FØRSTEINNTRYKK</strong></a>
        <nav aria-label="Hovedmeny"><a href="/#products">Produkter</a><a href="/bedrift">Bedrift</a><a href="/privat">Privat</a><a href="/#contact">Kontakt oss</a></nav>
      </header>
      <main>
        {page.kind === "home" ? <><StaticBrandHero /><MarketingIntro page={page} /></> : page.kind === "product" ? <ProductSearchIntro page={page} /> : <MarketingHero page={page} />}
        {page.kind === "product" ? <div className="search-container search-static-product"><img src={page.image} alt={page.imageAlt} width="400" height="400" /><p>Produktdesigneren åpnes når JavaScript er tilgjengelig. Du kan også <a href={`mailto:${business.email}`}>kontakte oss for å bestille</a>.</p></div> : null}
        {page.kind === "home" ? <section id="products" className="search-container search-static-products"><h2>Klær og produkter med ditt trykk</h2><PageLinks paths={pages.filter((entry) => entry.kind === "product").map((entry) => entry.path)} label="Produkter" /></section> : null}
        <MarketingBody page={page} />
        <SearchContact />
      </main>
      <footer className="search-static-footer search-container">{business.legalName} · Org.nr. {business.organizationNumber} · Kristiansand og hele Norge <a href="/terms">Vilkår</a></footer>
    </div>
  );
}
